import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Device } from '../types';
import { useDevices } from '../hooks/useDevices';
import { ApolloError } from '@apollo/client';
import { DocumentNode } from '@apollo/client';

interface DeviceRenderProps<T extends Device = Device> {
  devices: T[];
  filteredDevices: T[];
  loading: boolean;
  error: ApolloError | undefined;
  searchTerm: string;
  onSelectDevice: (device: T) => void;
  onBack: () => void;
  onRetry: () => void;
  onSearchChange: (searchTerm: string) => void;
}

interface BaseDevicesConfig<T extends Device = Device> {
  customFilterFunction?: (devices: T[], searchTerm: string) => T[];
  onSelectDevice: (device: T) => void;
  onBack?: () => void;
  children: (props: DeviceRenderProps<T>) => React.ReactNode;
}

export interface DevicesWithOverride<T extends Device = Device>
  extends BaseDevicesConfig<T> {
  overrideDefaults: true;
  graphqlQuery: DocumentNode; // Required when overrideDefaults is true
}

export interface DevicesWithoutOverride<T extends Device = Device>
  extends BaseDevicesConfig<T> {
  overrideDefaults?: false; // Or undefined
  graphqlQuery?: never; // Not required when overrideDefaults is false
}

export type DevicesProps<T extends Device = Device> =
  | DevicesWithOverride<T>
  | DevicesWithoutOverride<T>;

/**
 * Framework-agnostic Devices component using render props pattern.
 * This component handles device data fetching, search logic, and state management.
 * The UI rendering is completely controlled by the consumer through render props.
 *
 * @template T - The type of device objects (defaults to Device)
 */
export const Devices = React.memo(
  <T extends Device = Device>({
    onSelectDevice,
    onBack = () => {},
    children,
    ...config
  }: DevicesProps<T>): React.ReactElement => {
    const graphqlQuery = config.graphqlQuery; // Use the provided query or undefined (hook will default to GET_DEVICES)
    const customFilterFunction = config.customFilterFunction;

    const { devices, loading, error, fetchDevices } = useDevices({
      query: graphqlQuery,
    });
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
      fetchDevices();
    }, [fetchDevices]);

    // Filter devices based on search term using custom filter or default logic
    const filteredDevices = useMemo(() => {
      const deviceList = (devices as T[]) || [];

      if (customFilterFunction) {
        return customFilterFunction(deviceList, searchTerm);
      }

      if (!searchTerm) return deviceList; // Early return if no search term

      const lowerSearchTerm = searchTerm.toLowerCase();

      // Default filter logic - assumes devices have a 'name' property
      return deviceList.filter((device: T) =>
        device.name?.toLowerCase().includes(lowerSearchTerm),
      );
    }, [devices, searchTerm, customFilterFunction]);

    const handleRetry = useCallback(() => {
      fetchDevices();
    }, [fetchDevices]);

    const handleSearchChange = useCallback((newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);
    }, []);

    const renderProps = useMemo(
      () => ({
        devices: (devices as T[]) || [],
        filteredDevices,
        loading,
        error,
        searchTerm,
        onSelectDevice,
        onBack,
        onRetry: handleRetry,
        onSearchChange: handleSearchChange,
      }),
      [
        devices,
        filteredDevices,
        loading,
        error,
        searchTerm,
        onSelectDevice,
        onBack,
        handleRetry,
        handleSearchChange,
      ],
    );

    return children(renderProps) as React.ReactElement;
  },
) as <T extends Device = Device>(props: DevicesProps<T>) => React.ReactElement;
